import type { AppLocale } from "@/lib/i18n/constants";
import { isContentTextField, isRecord } from "@/lib/content/schema";
import type { ContentEntry, ContentObject, DeepContentPartial } from "@/lib/content/types";

/**
 * Owner-approved sensitive review-label translations, bound to the r2 review package.
 * This is deliberately a narrow locale-registry overlay: it never contains reviewerRole,
 * URLs, publisher fields, claims, credentials, payments, or client data.
 */
export const APPROVED_SENSITIVE_REVIEW_R2_MANIFEST = Object.freeze({
  candidateCommit: "2e924b9380d8af8b1d36eba56e4dd5f057e5b1fa",
  reportSha256: "c5e91177989b6200651bfba2f4d08ff916d4c4e0baa2c2d93ef0a97ce2a67403",
  responsesSha256: "881a955fa994b8d25583fd8f42c561fe130855a29a22648cd8fb9b9f43a1ce2d",
  selectedRowCount: 3108,
  bundleCount: 364,
  localeCount: 14,
  uniqueLocaleSourceCount: 168,
  excludedReviewerRoleCount: 367,
} as const);

const APPROVED_LOCALES = [
  "ar",
  "de",
  "es",
  "fr",
  "he",
  "hi",
  "id",
  "it",
  "ja",
  "ko",
  "pt-br",
  "ru",
  "tr",
  "zh-cn"
] as const satisfies readonly AppLocale[];
type ApprovedLocale = (typeof APPROVED_LOCALES)[number];
type SensitiveFamily = "health" | "adult";

type ReviewDefinition = Readonly<{
  entryKeys: readonly string[];
  sourceLabels: readonly string[];
}>;

const HEALTH_ENTRY_KEYS = [
  "industry-cluster:health-wellness:eeat-health-content",
  "industry-cluster:health-wellness:health-local-visibility",
  "industry-cluster:health-wellness:health-seo",
  "industry-cluster:health-wellness:medical-schema",
  "industry-cluster:health-wellness:regulated-claims-review",
  "industry-cluster:health-wellness:ymyl-content-review"
] as const;
const ADULT_ENTRY_KEYS = [
  "adult-platform:adult-ai-search-optimization",
  "adult-platform:adult-reputation-management",
  "adult-platform:adult-seo",
  "adult-platform:age-verification",
  "adult-platform:aup-explainer",
  "adult-platform:compliance",
  "adult-platform:creator-agency-services",
  "adult-platform:fan-platform-marketing",
  "adult-platform:fansly-strategy",
  "adult-platform:fanvue-growth",
  "adult-platform:instagram-adult-safe",
  "adult-platform:mainstream-platform-brand-safe-strategy",
  "adult-platform:onlyfans-marketing",
  "adult-platform:payment-processor",
  "adult-platform:performer-consent",
  "adult-platform:platform-policy",
  "adult-platform:pornhub-seo",
  "adult-platform:reddit-promotion",
  "adult-platform:tiktok-adult-safe",
  "adult-platform:youtube-shorts-adult-safe"
] as const;

const REVIEW_DEFINITIONS: Readonly<Record<SensitiveFamily, ReviewDefinition>> = Object.freeze({
  health: Object.freeze({
    entryKeys: HEALTH_ENTRY_KEYS,
    sourceLabels: [
  "Google Search Quality Rater Guidelines",
  "Google Search Central helpful content guidance",
  "Health Products Compliance Guidance",
  "General Wellness Policy for Low Risk Devices"
],
  }),
  adult: Object.freeze({
    entryKeys: ADULT_ENTRY_KEYS,
    sourceLabels: [
  "Google Search Central helpful content guidance",
  "Fanvue Acceptable Use Policy",
  "Reddit Rules",
  "TikTok sensitive and mature themes policy",
  "YouTube nudity and sexual content policy",
  "Meta adult nudity and sexual activity policy"
],
  }),
});

export const APPROVED_SENSITIVE_REVIEW_R2_TRANSLATIONS: Readonly<
  Record<ApprovedLocale, Readonly<Record<string, string>>>
> = Object.freeze({
  "ar": {
    "Reviewed by": "تمت المراجعة بواسطة",
    "Reviewed on": "تاريخ المراجعة",
    "Sources reviewed": "المصادر التي تمت مراجعتها",
    "Google Search Quality Rater Guidelines": "إرشادات Google لمقيّمي جودة البحث",
    "Google Search Central helpful content guidance": "إرشادات Google Search Central للمحتوى المفيد",
    "Health Products Compliance Guidance": "إرشادات الامتثال للمنتجات الصحية",
    "General Wellness Policy for Low Risk Devices": "سياسة العافية العامة للأجهزة منخفضة المخاطر",
    "Fanvue Acceptable Use Policy": "سياسة الاستخدام المقبول على Fanvue",
    "Reddit Rules": "قواعد Reddit",
    "TikTok sensitive and mature themes policy": "سياسة TikTok بشأن الموضوعات الحساسة والناضجة",
    "YouTube nudity and sexual content policy": "سياسة YouTube بشأن العري والمحتوى الجنسي",
    "Meta adult nudity and sexual activity policy": "سياسة Meta بشأن عري البالغين والنشاط الجنسي"
  },
  "de": {
    "Reviewed by": "Geprüft von",
    "Reviewed on": "Geprüft am",
    "Sources reviewed": "Überprüfte Quellen",
    "Google Search Quality Rater Guidelines": "Google-Richtlinien zur Bewertung der Suchqualität",
    "Google Search Central helpful content guidance": "Leitfaden zu hilfreichen Inhalten von Google Search Central",
    "Health Products Compliance Guidance": "Leitfaden zur Compliance bei Gesundheitsprodukten",
    "General Wellness Policy for Low Risk Devices": "Allgemeine Wellness-Richtlinie für Geräte mit geringem Risiko",
    "Fanvue Acceptable Use Policy": "Fanvue-Richtlinie zur akzeptablen Nutzung",
    "Reddit Rules": "Reddit-Regeln",
    "TikTok sensitive and mature themes policy": "TikTok-Richtlinie zu sensiblen und reifen Themen",
    "YouTube nudity and sexual content policy": "YouTube-Richtlinie zu Nacktheit und sexuellem Inhalt",
    "Meta adult nudity and sexual activity policy": "Meta-Richtlinie zu Nacktheit und sexueller Aktivität für Erwachsene"
  },
  "es": {
    "Reviewed by": "Revisado por",
    "Reviewed on": "Revisado el",
    "Sources reviewed": "Fuentes revisadas",
    "Google Search Quality Rater Guidelines": "Directrices de calidad de búsqueda de Google para evaluadores",
    "Google Search Central helpful content guidance": "Guía de contenido útil de Google Search Central",
    "Health Products Compliance Guidance": "Orientación sobre cumplimiento de productos sanitarios",
    "General Wellness Policy for Low Risk Devices": "Política general de bienestar para dispositivos de bajo riesgo",
    "Fanvue Acceptable Use Policy": "Política de uso aceptable de Fanvue",
    "Reddit Rules": "Reglas de Reddit",
    "TikTok sensitive and mature themes policy": "Política de TikTok sobre temas sensibles y para adultos",
    "YouTube nudity and sexual content policy": "Política de YouTube sobre desnudez y contenido sexual",
    "Meta adult nudity and sexual activity policy": "Política de Meta sobre desnudez para adultos y actividad sexual"
  },
  "fr": {
    "Reviewed by": "Revu par",
    "Reviewed on": "Revu le",
    "Sources reviewed": "Sources consultées",
    "Google Search Quality Rater Guidelines": "Directives Google sur la qualité de la recherche pour les évaluateurs",
    "Google Search Central helpful content guidance": "Guide sur le contenu utile de Google Search Central",
    "Health Products Compliance Guidance": "Orientations sur la conformité des produits de santé",
    "General Wellness Policy for Low Risk Devices": "Politique générale de bien-être pour les dispositifs à faible risque",
    "Fanvue Acceptable Use Policy": "Politique d'utilisation acceptable de Fanvue",
    "Reddit Rules": "Règles de Reddit",
    "TikTok sensitive and mature themes policy": "Politique TikTok sur les thèmes sensibles et matures",
    "YouTube nudity and sexual content policy": "Politique YouTube sur la nudité et le contenu sexuel",
    "Meta adult nudity and sexual activity policy": "Politique Meta sur la nudité adulte et l'activité sexuelle"
  },
  "he": {
    "Reviewed by": "נבדק על ידי",
    "Reviewed on": "נבדק בתאריך",
    "Sources reviewed": "מקורות שנבדקו",
    "Google Search Quality Rater Guidelines": "הנחיות Google למדרגי איכות החיפוש",
    "Google Search Central helpful content guidance": "הנחיות Google Search Central לתוכן מועיל",
    "Health Products Compliance Guidance": "הנחיות תאימות למוצרי בריאות",
    "General Wellness Policy for Low Risk Devices": "מדיניות וולנס כללית למכשירים בסיכון נמוך",
    "Fanvue Acceptable Use Policy": "מדיניות שימוש מקובל של Fanvue",
    "Reddit Rules": "כללי Reddit",
    "TikTok sensitive and mature themes policy": "מדיניות TikTok לנושאים רגישים ובוגרים",
    "YouTube nudity and sexual content policy": "מדיניות YouTube על עירום ותוכן מיני",
    "Meta adult nudity and sexual activity policy": "מדיניות Meta על עירום ופעילות מינית למבוגרים"
  },
  "hi": {
    "Reviewed by": "समीक्षक",
    "Reviewed on": "समीक्षा तिथि",
    "Sources reviewed": "समीक्षित स्रोत",
    "Google Search Quality Rater Guidelines": "Google खोज गुणवत्ता मूल्यांकनकर्ता दिशानिर्देश",
    "Google Search Central helpful content guidance": "Google Search Central की उपयोगी सामग्री संबंधी मार्गदर्शिका",
    "Health Products Compliance Guidance": "स्वास्थ्य उत्पाद अनुपालन मार्गदर्शन",
    "General Wellness Policy for Low Risk Devices": "निम्न जोखिम वाले उपकरणों के लिए सामान्य वेलनेस नीति",
    "Fanvue Acceptable Use Policy": "Fanvue स्वीकार्य उपयोग नीति",
    "Reddit Rules": "Reddit नियम",
    "TikTok sensitive and mature themes policy": "TikTok संवेदनशील और परिपक्व विषयों की नीति",
    "YouTube nudity and sexual content policy": "YouTube नग्नता और यौन सामग्री नीति",
    "Meta adult nudity and sexual activity policy": "Meta वयस्क नग्नता और यौन गतिविधि नीति"
  },
  "id": {
    "Reviewed by": "Diulas oleh",
    "Reviewed on": "Diulas pada",
    "Sources reviewed": "Sumber yang diulas",
    "Google Search Quality Rater Guidelines": "Panduan Google untuk Penilai Kualitas Penelusuran",
    "Google Search Central helpful content guidance": "Panduan Google Search Central tentang konten bermanfaat",
    "Health Products Compliance Guidance": "Panduan kepatuhan produk kesehatan",
    "General Wellness Policy for Low Risk Devices": "Kebijakan kesehatan umum untuk perangkat berisiko rendah",
    "Fanvue Acceptable Use Policy": "Kebijakan penggunaan yang dapat diterima Fanvue",
    "Reddit Rules": "Aturan Reddit",
    "TikTok sensitive and mature themes policy": "Kebijakan tema sensitif dan dewasa TikTok",
    "YouTube nudity and sexual content policy": "Kebijakan konten ketelanjangan dan seksual YouTube",
    "Meta adult nudity and sexual activity policy": "Kebijakan ketelanjangan dan aktivitas seksual dewasa Meta"
  },
  "it": {
    "Reviewed by": "Verificato da",
    "Reviewed on": "Verificato il",
    "Sources reviewed": "Fonti verificate",
    "Google Search Quality Rater Guidelines": "Linee guida Google sulla qualità della ricerca per i valutatori",
    "Google Search Central helpful content guidance": "Guida ai contenuti utili di Google Search Central",
    "Health Products Compliance Guidance": "Guida alla conformità dei prodotti sanitari",
    "General Wellness Policy for Low Risk Devices": "Politica generale sul benessere per dispositivi a basso rischio",
    "Fanvue Acceptable Use Policy": "Politica di uso accettabile di Fanvue",
    "Reddit Rules": "Regole di Reddit",
    "TikTok sensitive and mature themes policy": "Politica TikTok su temi sensibili e per adulti",
    "YouTube nudity and sexual content policy": "Politica YouTube su nudità e contenuti sessuali",
    "Meta adult nudity and sexual activity policy": "Politica Meta su nudità per adulti e attività sessuale"
  },
  "ja": {
    "Reviewed by": "レビュー者",
    "Reviewed on": "レビュー日",
    "Sources reviewed": "参照したソース",
    "Google Search Quality Rater Guidelines": "Google 検索品質評価者向けガイドライン",
    "Google Search Central helpful content guidance": "Google Search Centralの有用なコンテンツに関するガイダンス",
    "Health Products Compliance Guidance": "健康関連製品のコンプライアンスガイダンス",
    "General Wellness Policy for Low Risk Devices": "低リスク機器向け一般ウェルネスポリシー",
    "Fanvue Acceptable Use Policy": "Fanvueの許容される利用ポリシー",
    "Reddit Rules": "Redditルール",
    "TikTok sensitive and mature themes policy": "TikTokのセンシティブ・成熟テーマに関するポリシー",
    "YouTube nudity and sexual content policy": "YouTubeのヌードおよび性的コンテンツに関するポリシー",
    "Meta adult nudity and sexual activity policy": "Metaの成人向けヌードおよび性行為に関するポリシー"
  },
  "ko": {
    "Reviewed by": "검토자",
    "Reviewed on": "검토일",
    "Sources reviewed": "검토한 출처",
    "Google Search Quality Rater Guidelines": "Google 검색 품질 평가자 가이드라인",
    "Google Search Central helpful content guidance": "Google Search Central 유용한 콘텐츠 가이드",
    "Health Products Compliance Guidance": "건강 제품 준수 가이드",
    "General Wellness Policy for Low Risk Devices": "저위험 기기용 일반 웰니스 정책",
    "Fanvue Acceptable Use Policy": "Fanvue 허용 사용 정책",
    "Reddit Rules": "Reddit 규칙",
    "TikTok sensitive and mature themes policy": "TikTok 민감 및 성숙 테마 정책",
    "YouTube nudity and sexual content policy": "YouTube 누드 및 성적 콘텐츠 정책",
    "Meta adult nudity and sexual activity policy": "Meta 성인 누드 및 성행위 정책"
  },
  "pt-br": {
    "Reviewed by": "Revisado por",
    "Reviewed on": "Revisado em",
    "Sources reviewed": "Fontes revisadas",
    "Google Search Quality Rater Guidelines": "Diretrizes do Google sobre qualidade da pesquisa para avaliadores",
    "Google Search Central helpful content guidance": "Orientação sobre conteúdo útil do Google Search Central",
    "Health Products Compliance Guidance": "Orientação sobre conformidade de produtos de saúde",
    "General Wellness Policy for Low Risk Devices": "Política geral de bem-estar para dispositivos de baixo risco",
    "Fanvue Acceptable Use Policy": "Política de uso aceitável da Fanvue",
    "Reddit Rules": "Regras do Reddit",
    "TikTok sensitive and mature themes policy": "Política do TikTok sobre temas sensíveis e maduros",
    "YouTube nudity and sexual content policy": "Política do YouTube sobre nudez e conteúdo sexual",
    "Meta adult nudity and sexual activity policy": "Política da Meta sobre nudez adulta e atividade sexual"
  },
  "ru": {
    "Reviewed by": "Рецензент",
    "Reviewed on": "Дата проверки",
    "Sources reviewed": "Проверенные источники",
    "Google Search Quality Rater Guidelines": "Руководство Google по оценке качества поиска",
    "Google Search Central helpful content guidance": "Рекомендации Google Search Central по полезному контенту",
    "Health Products Compliance Guidance": "Руководство по соответствию требованиям для продуктов здоровья",
    "General Wellness Policy for Low Risk Devices": "Политика общего велнеса для устройств с низким уровнем риска",
    "Fanvue Acceptable Use Policy": "Политика допустимого использования Fanvue",
    "Reddit Rules": "Правила Reddit",
    "TikTok sensitive and mature themes policy": "Политика TikTok о деликатных и взрослых темах",
    "YouTube nudity and sexual content policy": "Политика YouTube о наготе и сексуальном контенте",
    "Meta adult nudity and sexual activity policy": "Политика Meta о наготе и сексуальной активности для взрослых"
  },
  "tr": {
    "Reviewed by": "İnceleyen",
    "Reviewed on": "İnceleme tarihi",
    "Sources reviewed": "İncelenen kaynaklar",
    "Google Search Quality Rater Guidelines": "Google Arama Kalitesi Değerlendirici Yönergeleri",
    "Google Search Central helpful content guidance": "Google Search Central yararlı içerik rehberi",
    "Health Products Compliance Guidance": "Sağlık ürünleri uyumluluk rehberi",
    "General Wellness Policy for Low Risk Devices": "Düşük riskli cihazlar için genel sağlık politikası",
    "Fanvue Acceptable Use Policy": "Fanvue kabul edilebilir kullanım politikası",
    "Reddit Rules": "Reddit Kuralları",
    "TikTok sensitive and mature themes policy": "TikTok hassas ve olgun temalar politikası",
    "YouTube nudity and sexual content policy": "YouTube çıplaklık ve cinsel içerik politikası",
    "Meta adult nudity and sexual activity policy": "Meta yetişkin çıplaklık ve cinsel aktivite politikası"
  },
  "zh-cn": {
    "Reviewed by": "审阅者",
    "Reviewed on": "审阅日期",
    "Sources reviewed": "已审阅来源",
    "Google Search Quality Rater Guidelines": "Google 搜索质量评估员指南",
    "Google Search Central helpful content guidance": "Google Search Central 实用内容指南",
    "Health Products Compliance Guidance": "健康产品合规指南",
    "General Wellness Policy for Low Risk Devices": "低风险设备一般健康政策",
    "Fanvue Acceptable Use Policy": "Fanvue 可接受使用政策",
    "Reddit Rules": "Reddit 规则",
    "TikTok sensitive and mature themes policy": "TikTok 敏感与成熟主题政策",
    "YouTube nudity and sexual content policy": "YouTube 裸露与色情内容政策",
    "Meta adult nudity and sexual activity policy": "Meta 成人裸露与性行为政策"
  }
});

function fail(message: string): never {
  throw new Error(`Approved sensitive review r2: ${message}`);
}
function isApprovedLocale(locale: AppLocale): locale is ApprovedLocale {
  return (APPROVED_LOCALES as readonly AppLocale[]).includes(locale);
}

function familyForEntry(entryKey: string): SensitiveFamily | undefined {
  if ((HEALTH_ENTRY_KEYS as readonly string[]).includes(entryKey)) return "health";
  if ((ADULT_ENTRY_KEYS as readonly string[]).includes(entryKey)) return "adult";
  return undefined;
}

function approvedText(locale: ApprovedLocale, sourceText: string) {
  const value = APPROVED_SENSITIVE_REVIEW_R2_TRANSLATIONS[locale][sourceText];
  if (typeof value !== "string" || value.length === 0) {
    fail(`missing approved text for ${locale}/${sourceText}`);
  }
  return Object.freeze({ value, _status: "ai_final" as const, _lang: locale });
}

function assertBaseText(value: unknown, expected: string, label: string): void {
  if (!isContentTextField(value) || value.value !== expected) {
    fail(`${label} does not match the approved r2 source text`);
  }
}

function assertRecord(value: unknown, label: string): Record<string, unknown> {
  if (!isRecord(value) || Array.isArray(value)) {
    fail(`${label} must be an object`);
  }
  return value;
}

function assertContract(): void {
  const healthSlots = HEALTH_ENTRY_KEYS.length * (3 + REVIEW_DEFINITIONS.health.sourceLabels.length);
  const adultSlots = ADULT_ENTRY_KEYS.length * (3 + REVIEW_DEFINITIONS.adult.sourceLabels.length);
  const totalSlots = (healthSlots + adultSlots) * APPROVED_LOCALES.length;
  const uniqueLocaleSourceCount = Object.values(APPROVED_SENSITIVE_REVIEW_R2_TRANSLATIONS)
    .reduce((count, catalog) => count + Object.keys(catalog).length, 0);

  if (healthSlots !== 42 || adultSlots !== 180 || totalSlots !== APPROVED_SENSITIVE_REVIEW_R2_MANIFEST.selectedRowCount) {
    fail("slot-count contract is invalid");
  }
  if (uniqueLocaleSourceCount !== APPROVED_SENSITIVE_REVIEW_R2_MANIFEST.uniqueLocaleSourceCount) {
    fail("unique locale/source contract is invalid");
  }
  if (Object.keys(APPROVED_SENSITIVE_REVIEW_R2_TRANSLATIONS).length !== APPROVED_SENSITIVE_REVIEW_R2_MANIFEST.localeCount) {
    fail("locale-count contract is invalid");
  }
}

assertContract();

export function isApprovedSensitiveReviewEntry(entryKey: string): boolean {
  return familyForEntry(entryKey) !== undefined;
}

export function getApprovedSensitiveReviewContract() {
  return Object.freeze({
    locales: APPROVED_LOCALES,
    healthEntryKeys: HEALTH_ENTRY_KEYS,
    adultEntryKeys: ADULT_ENTRY_KEYS,
    healthSourceLabels: REVIEW_DEFINITIONS.health.sourceLabels,
    adultSourceLabels: REVIEW_DEFINITIONS.adult.sourceLabels,
    ...APPROVED_SENSITIVE_REVIEW_R2_MANIFEST,
  });
}

/**
 * Creates only the approved review-label overlay for one locale/entry pair.
 * review.sources is cloned from the English base before each label is replaced, so
 * source URL, publisher, and other static fields survive the array-atomic resolver.
 */
export function getApprovedSensitiveReviewOverride(
  entry: ContentEntry,
  locale: AppLocale,
): DeepContentPartial<ContentObject> | undefined {
  if (!isApprovedLocale(locale)) return undefined;

  const family = familyForEntry(entry.key);
  if (!family) return undefined;

  const definition = REVIEW_DEFINITIONS[family];
  const baseReview = assertRecord(entry.base.review, `${entry.key}.base.review`);
  assertBaseText(baseReview.reviewedByLabel, "Reviewed by", `${entry.key}.reviewedByLabel`);
  assertBaseText(baseReview.reviewedOnLabel, "Reviewed on", `${entry.key}.reviewedOnLabel`);
  assertBaseText(baseReview.sourcesLabel, "Sources reviewed", `${entry.key}.sourcesLabel`);

  const baseSources = baseReview.sources;
  if (!Array.isArray(baseSources) || baseSources.length !== definition.sourceLabels.length) {
    fail(`${entry.key}.review.sources does not match the approved r2 source count`);
  }

  const sources = baseSources.map((source, index) => {
    const baseSource = assertRecord(source, `${entry.key}.review.sources.${index}`);
    const expectedSourceLabel = definition.sourceLabels[index];
    assertBaseText(baseSource.label, expectedSourceLabel, `${entry.key}.review.sources.${index}.label`);
    return Object.freeze({
      ...baseSource,
      label: approvedText(locale, expectedSourceLabel),
    });
  });

  return Object.freeze({
    review: Object.freeze({
      reviewedByLabel: approvedText(locale, "Reviewed by"),
      reviewedOnLabel: approvedText(locale, "Reviewed on"),
      sourcesLabel: approvedText(locale, "Sources reviewed"),
      sources,
    }),
  }) as DeepContentPartial<ContentObject>;
}
